import type { Conversation, Tenant, TraceStep, TraceSummary } from './types'

type StepConfig = { label: string; color: string; icon: string }

export const STEP_TYPES = {
  RECEIVE: { label: 'Mensaje recibido', color: '#6366f1', icon: 'IN' },
  ORCHESTRATOR: { label: 'Orquestador', color: '#8b5cf6', icon: 'OR' },
  COMPLEXITY: { label: 'Router de complejidad', color: '#a78bfa', icon: 'CX' },
  DECISION: { label: 'Decision Engine', color: '#06b6d4', icon: 'DE' },
  ROUTING_PROBABILISTIC_DECISION: { label: 'Probabilistic router', color: '#0f766e', icon: 'PR' },
  STRATEGY_RECOMMENDATION: { label: 'Strategy recommendation', color: '#14b8a6', icon: 'SR' },
  TOOL_CALL: { label: 'Tool call', color: '#10b981', icon: 'TL' },
  TOOL_EXECUTION: { label: 'Tool execution', color: '#14b8a6', icon: 'TX' },
  CACHE: { label: 'Cache hit', color: '#f59e0b', icon: 'CH' },
  LLM: { label: 'LLM call', color: '#ec4899', icon: 'AI' },
  FALLBACK: { label: 'Fallback a waterfall', color: '#f97316', icon: 'FB' },
  ESCALATE: { label: 'Escalamiento', color: '#ef4444', icon: 'UP' },
  RESPOND: { label: 'Respuesta enviada', color: '#22c55e', icon: 'OK' },
  ERROR: { label: 'Error', color: '#dc2626', icon: 'ER' },
  AGENT_GRAPH: { label: 'Agent graph', color: '#7c3aed', icon: 'AG' },
  RUNTIME_ROUTING_DECISION: { label: 'Runtime routing', color: '#0ea5e9', icon: 'RT' },
  INITIAL_GRAPH_ROUTE: { label: 'Initial graph route', color: '#38bdf8', icon: 'IR' },
  FINAL_EFFECTIVE_ROUTE: { label: 'Final effective route', color: '#22c55e', icon: 'FR' },
  GRAPH_CHECKPOINT_LOAD: { label: 'Checkpoint load', color: '#60a5fa', icon: 'CL' },
  GRAPH_CHECKPOINT_SAVE: { label: 'Checkpoint save', color: '#34d399', icon: 'CS' },
  GRAPH_CHECKPOINT_LOAD_FAILED: { label: 'Checkpoint load failed', color: '#f59e0b', icon: 'CF' },
  GRAPH_STATE_RESTORED: { label: 'State restored', color: '#3b82f6', icon: 'SR' },
  GRAPH_HITL_INTERRUPTED: { label: 'HITL interrupt', color: '#ef4444', icon: 'HI' },
  HITL_INTERRUPT_TRIGGERED: { label: 'HITL trigger', color: '#f97316', icon: 'HT' },
  HITL_INTERRUPT_RESUMED: { label: 'HITL resumed', color: '#22c55e', icon: 'HR' },
  HITL_RESUME: { label: 'HITL resume action', color: '#16a34a', icon: 'RA' },
  LANGGRAPH_NODE_ENTER: { label: 'LangGraph enter', color: '#a855f7', icon: 'NE' },
  LANGGRAPH_NODE_EXIT: { label: 'LangGraph exit', color: '#c084fc', icon: 'NX' },
  SUPERVISOR_REDIRECT: { label: 'Supervisor redirect', color: '#fb7185', icon: 'RD' },
  SUPERVISOR_OVERRIDE: { label: 'Supervisor override', color: '#f472b6', icon: 'SO' },
  INTENT_TRAJECTORY_SIGNAL_DETECTED: { label: 'Intent trajectory', color: '#38bdf8', icon: 'IT' },
  ESCALATION_REQUESTED: { label: 'Escalation requested', color: '#f97316', icon: 'RQ' },
  ESCALATION_PROPAGATED: { label: 'Escalation propagated', color: '#fb7185', icon: 'PG' },
  ESCALATION_HANDOFF_TRIGGERED: { label: 'Handoff triggered', color: '#ef4444', icon: 'HF' },
  REENGAGEMENT_SUGGESTED: { label: 'Re-engagement suggested', color: '#f59e0b', icon: 'RS' },
  REENGAGEMENT_SCHEDULED: { label: 'Re-engagement scheduled', color: '#14b8a6', icon: 'RJ' },
  REENGAGEMENT_CANCELLED: { label: 'Re-engagement cancelled', color: '#64748b', icon: 'RC' },
  RUNTIME_SWITCH: { label: 'Runtime switch', color: '#06b6d4', icon: 'SW' },
  STATE_VIEW_RESOLVED: { label: 'State view resolved', color: '#6366f1', icon: 'SV' },
  GUARDRAIL_OVERRIDE: { label: 'Guardrail override', color: '#e11d48', icon: 'GR' },
  FUNNEL_AUTHORITY: { label: 'Funnel authority snapshot', color: '#0ea5e9', icon: 'FA' },
  SUPERVISOR_DRIFT_DETECTED: { label: 'Supervisor drift detected', color: '#f59e0b', icon: 'SD' },
  SUPERVISOR_REFLEXION_REDIRECT: { label: 'Supervisor reflexion redirect', color: '#d946ef', icon: 'SR' },
} as const satisfies Record<string, StepConfig>

const RELATED_FILES: Record<string, string[]> = {
  RECEIVE: ['backend/src/services/botOrchestrator.service.ts'],
  ORCHESTRATOR: ['backend/src/services/botOrchestrator.service.ts'],
  COMPLEXITY: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/complexityRouter.ts'],
  DECISION: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/services/botDecisionEngine.ts'],
  ROUTING_PROBABILISTIC_DECISION: ['backend/src/services/botDecisionEngine.ts', 'backend/src/services/ai/salesRoutingProbabilityModel.ts'],
  STRATEGY_RECOMMENDATION: ['backend/src/services/botDecisionEngine.ts', 'backend/src/services/ai/salesRoutingProbabilityModel.ts'],
  TOOL_CALL: ['backend/src/services/ai/agentToolExecutor.ts'],
  TOOL_EXECUTION: ['backend/src/services/langGraph/toolNode/governedToolNode.ts'],
  CACHE: ['backend/src/services/langGraph/toolNode/governedToolNode.ts', 'backend/src/services/ai/agentToolExecutor.ts'],
  LLM: ['backend/src/services/responseManager.ts', 'backend/src/services/ai/agentToolExecutor.ts', 'backend/src/config/aiClient.ts'],
  FALLBACK: ['backend/src/services/responseManager.ts', 'backend/src/services/ai/agentToolExecutor.ts'],
  ESCALATE: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/services/responseManager.ts'],
  RESPOND: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/services/responseManager.ts'],
  ERROR: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/services/responseManager.ts'],
  AGENT_GRAPH: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/services/langGraph/graph.ts'],
  RUNTIME_ROUTING_DECISION: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/services/langGraph/router.ts', 'backend/src/services/langGraph/runtimeSelection.ts'],
  INITIAL_GRAPH_ROUTE: ['backend/src/services/langGraph/graph.ts', 'backend/src/services/botOrchestrator.service.ts'],
  FINAL_EFFECTIVE_ROUTE: ['backend/src/services/langGraph/graph.ts', 'backend/src/services/botOrchestrator.service.ts'],
  GRAPH_CHECKPOINT_LOAD: ['backend/src/services/langGraph/checkpointer.ts', 'backend/src/services/langGraph/graph.ts'],
  GRAPH_CHECKPOINT_SAVE: ['backend/src/services/langGraph/checkpointer.ts', 'backend/src/services/langGraph/graph.ts'],
  GRAPH_CHECKPOINT_LOAD_FAILED: ['backend/src/services/langGraph/checkpointer.ts', 'backend/src/services/langGraph/graph.ts'],
  GRAPH_STATE_RESTORED: ['backend/src/services/langGraph/checkpointer.ts', 'backend/src/services/langGraph/graph.ts'],
  GRAPH_HITL_INTERRUPTED: ['backend/src/services/langGraph/graph.ts', 'backend/src/services/langGraph/nodes/humanHandoffInterrupt.node.ts'],
  HITL_INTERRUPT_TRIGGERED: ['backend/src/services/langGraph/nodes/humanHandoffInterrupt.node.ts'],
  HITL_INTERRUPT_RESUMED: ['backend/src/services/langGraph/nodes/humanHandoffInterrupt.node.ts'],
  HITL_RESUME: ['backend/src/services/langGraph/nodes/humanHandoffInterrupt.node.ts'],
  LANGGRAPH_NODE_ENTER: ['backend/src/services/langGraph/tracing/traceEmitter.ts', 'backend/src/services/langGraph/graph.ts'],
  LANGGRAPH_NODE_EXIT: ['backend/src/services/langGraph/tracing/traceEmitter.ts', 'backend/src/services/langGraph/graph.ts'],
  SUPERVISOR_REDIRECT: ['backend/src/services/langGraph/nodes/supervisor.node.ts', 'backend/src/services/langGraph/graph.ts'],
  SUPERVISOR_OVERRIDE: ['backend/src/services/langGraph/nodes/supervisor.node.ts', 'backend/src/services/langGraph/graph.ts'],
  INTENT_TRAJECTORY_SIGNAL_DETECTED: ['backend/src/services/langGraph/nodes/supervisor.node.ts', 'backend/src/services/langGraph/strategy/salesStrategyEngine.ts'],
  ESCALATION_REQUESTED: ['backend/src/services/langGraph/nodes/supervisor.node.ts', 'backend/src/services/botOrchestrator.service.ts'],
  ESCALATION_PROPAGATED: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/services/langGraph/nodes/supervisor.node.ts'],
  ESCALATION_HANDOFF_TRIGGERED: ['backend/src/services/botOrchestrator.service.ts'],
  REENGAGEMENT_SUGGESTED: ['backend/src/jobs/reengagementJob.ts', 'backend/src/services/botOrchestrator.service.ts'],
  REENGAGEMENT_SCHEDULED: ['backend/src/jobs/reengagementJob.ts', 'backend/src/services/botOrchestrator.service.ts'],
  REENGAGEMENT_CANCELLED: ['backend/src/jobs/reengagementJob.ts', 'backend/src/services/botOrchestrator.service.ts'],
  RUNTIME_SWITCH: ['backend/src/services/botOrchestrator.service.ts', 'backend/src/modules/wa/messageProcessor.ts'],
  STATE_VIEW_RESOLVED: ['backend/src/services/langGraph/stateView/salesStateView.ts'],
  GUARDRAIL_OVERRIDE: ['backend/src/services/agentGraph/guards/consistencyGuard.ts', 'backend/src/services/langGraph/nodes/phase6.utils.ts'],
  FUNNEL_AUTHORITY: ['backend/src/services/langGraph/graph.ts'],
  SUPERVISOR_DRIFT_DETECTED: ['backend/src/services/langGraph/supervisor/supervisorDriftDetector.ts', 'backend/src/services/langGraph/nodes/supervisor.node.ts'],
  SUPERVISOR_REFLEXION_REDIRECT: ['backend/src/services/langGraph/supervisor/supervisorReflexionCritic.ts', 'backend/src/services/langGraph/nodes/supervisor.node.ts'],
  searchProduct: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/tools/searchProduct.tool.ts'],
  checkStock: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/tools/checkStock.tool.ts'],
  checkPromotions: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/tools/checkPromotions.tool.ts'],
  getTenantInfo: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/tools/getTenantInfo.tool.ts'],
  getConversationSummary: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/tools/getConversationSummary.tool.ts'],
  getProductRecommendations: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/tools/getProductRecommendations.tool.ts'],
  escalateHuman: ['backend/src/services/ai/agentToolExecutor.ts', 'backend/src/services/ai/tools/escalateHuman.tool.ts'],
}

function getStepConfig(stepType: string): StepConfig {
  return (STEP_TYPES as Record<string, StepConfig>)[stepType] || STEP_TYPES.ORCHESTRATOR
}

function cleanText(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim()
}

function truncate(value: unknown, max = 180): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value)
  return text.length > max ? `${text.slice(0, max)}...` : text
}

function normalizeSupervisorCategory(step: TraceStep): string | null {
  if (step.type !== 'SUPERVISOR_OVERRIDE') return null

  const action = String(step.data.action ?? '').trim().toLowerCase()
  const reason = String(step.data.reason ?? '').trim().toLowerCase()
  const category = action || reason

  if (category === 'support_objection' || category === 'strong_objection') return 'support_objection'
  if (category === 'acknowledge_rejection' || category === 'real_rejection') return 'acknowledge_rejection'
  if (action) return action
  return null
}

export function parseRawResponse(rawResponse: unknown): Record<string, unknown> | null {
  if (typeof rawResponse !== 'string' || !rawResponse.trim()) return null
  try {
    return JSON.parse(rawResponse.replace(/^```json\s*/i, '').replace(/```$/i, '').trim())
  } catch {
    return null
  }
}

export function getLlmAnalysis(step: TraceStep): Array<{ label: string; value: string }> {
  if (step.type !== 'LLM') return []

  const parsed = parseRawResponse(step.data.rawResponse)
  if (!parsed) {
    return typeof step.data.rawResponse === 'string' && step.data.rawResponse.trim()
      ? [{ label: 'format', value: 'text' }]
      : []
  }

  const analysis: Array<{ label: string; value: string }> = []

  if (typeof parsed.tool === 'string') analysis.push({ label: 'tool', value: parsed.tool })
  if (typeof parsed.action === 'string') analysis.push({ label: 'action', value: parsed.action })
  if (typeof parsed.reason === 'string' && parsed.reason) analysis.push({ label: 'reason', value: parsed.reason })
  if (typeof parsed.message === 'string' && parsed.message) analysis.push({ label: 'message', value: parsed.message })

  return analysis
}

export function getRelatedFiles(step: TraceStep): string[] {
  if (step.type === 'TOOL_EXECUTION') {
    return RELATED_FILES.TOOL_EXECUTION
  }

  if (step.type === 'TOOL_CALL' || step.type === 'CACHE') {
    const tool = String(step.data.tool ?? step.data.toolName ?? '')
    return RELATED_FILES[tool] || RELATED_FILES[step.type] || ['backend/src/services/ai/agentToolExecutor.ts']
  }

  const parsed = step.type === 'LLM' ? parseRawResponse(step.data.rawResponse) : null
  if (parsed && typeof parsed.tool === 'string' && RELATED_FILES[parsed.tool]) {
    return [...RELATED_FILES.LLM, ...RELATED_FILES[parsed.tool]]
  }

  return RELATED_FILES[step.type] || ['backend/src/services/decisionTrace.service.ts']
}

export function getStepTools(step: TraceStep): string[] {
  if (step.type === 'LLM') {
    const parsed = parseRawResponse(step.data.rawResponse)
    if (typeof parsed?.tool === 'string') return [parsed.tool]
    if (Array.isArray(parsed?.tools)) {
      return parsed.tools.filter((tool: unknown): tool is string => typeof tool === 'string')
    }
  }

  if (step.type === 'TOOL_CALL' || step.type === 'CACHE' || step.type === 'TOOL_EXECUTION') {
    const tool = step.data.toolName ?? step.data.tool
    return typeof tool === 'string' ? [tool] : []
  }

  if (step.type === 'COMPLEXITY' && Array.isArray(step.data.suggestedTools)) {
    return step.data.suggestedTools.filter((tool: unknown): tool is string => typeof tool === 'string')
  }

  if (Array.isArray(step.data.toolsUsed)) {
    return step.data.toolsUsed.filter((tool: unknown): tool is string => typeof tool === 'string')
  }

  return []
}

export function summarizeStep(step: TraceStep): string {
  if (step.type === 'RECEIVE') return cleanText(step.data.message) || 'Mensaje recibido'
  if (step.type === 'ORCHESTRATOR') return `${step.data.action || 'processMessage'} iniciado`
  if (step.type === 'DECISION') return `strategy: '${step.data.strategy || 'unknown'}' · ${step.data.reason || 'sin razon'}`
  if (step.type === 'COMPLEXITY') return `level: '${step.data.level || 'unknown'}'`
  if (step.type === 'RUNTIME_ROUTING_DECISION') return `${step.data.runtime || 'runtime'} · ${step.data.reason || 'sin razon'}`
  if (step.type === 'INITIAL_GRAPH_ROUTE') return `initial -> ${step.data.route || 'unknown'}`
  if (step.type === 'FINAL_EFFECTIVE_ROUTE') return `final -> ${step.data.finalRoute || 'unknown'}`
  if (step.type === 'GRAPH_CHECKPOINT_LOAD') return `${step.data.restored ? 'restored' : 'fresh'} checkpoint`
  if (step.type === 'GRAPH_CHECKPOINT_SAVE') return `${step.data.saved ? 'saved' : 'not saved'} checkpoint`
  if (step.type === 'GRAPH_CHECKPOINT_LOAD_FAILED') return step.data.reason || step.data.error || 'checkpoint load failed'
  if (step.type === 'GRAPH_STATE_RESTORED') return `checkpoint ${step.data.checkpointId || 'restored'}`
  if (step.type === 'GRAPH_HITL_INTERRUPTED') return `pause -> ${step.data.hitlRequest?.reason || 'hitl'}`
  if (step.type === 'HITL_INTERRUPT_TRIGGERED') return `trigger -> ${step.data.reason || 'hitl'}`
  if (step.type === 'HITL_INTERRUPT_RESUMED') return `resume -> ${step.data.action || 'approve'}`
  if (step.type === 'HITL_RESUME') return `action -> ${step.data.action || 'approve'}`
  if (step.type === 'LANGGRAPH_NODE_ENTER') return `${step.data.nodeName || 'node'} enter`
  if (step.type === 'LANGGRAPH_NODE_EXIT') return `${step.data.nodeName || 'node'} exit${step.data.failed ? ' · failed' : ''}`
  if (step.type === 'TOOL_EXECUTION') return `${step.data.toolName || step.data.tool || 'tool'} · ${step.data.cacheHit ? 'CACHE HIT' : 'EXEC'}`
  if (step.type === 'TOOL_CALL' || step.type === 'CACHE') return `${step.data.tool || 'tool'} · ${step.data.cacheHit ? 'CACHE HIT' : 'EXEC'}`
  if (step.type === 'LLM') return `${step.data.provider || 'provider'} · ${step.data.model || 'model'}`
  if (step.type === 'FALLBACK') return `fallback -> ${step.data.fallbackTo || 'waterfall'}`
  if (step.type === 'RESPOND') return cleanText(step.data.message) || 'Respuesta enviada'
  if (step.type === 'ESCALATE') return 'Escalamiento'
  if (step.type === 'ESCALATION_REQUESTED') return cleanText(step.data.reason) || 'Escalation requested'
  if (step.type === 'ESCALATION_PROPAGATED') return cleanText(step.data.reason) || 'Escalation propagated'
  if (step.type === 'ESCALATION_HANDOFF_TRIGGERED') return cleanText(step.data.reason) || 'Handoff triggered'
  if (step.type === 'REENGAGEMENT_SUGGESTED') return `suggested · ${step.data.stage || 'stage'} · ${step.data.stagnationTurns ?? 0} turns`
  if (step.type === 'REENGAGEMENT_SCHEDULED') return `scheduled · ${step.data.timeoutMinutes || 0}m`
  if (step.type === 'REENGAGEMENT_CANCELLED') return `cancelled · ${step.data.cancelledCount ?? 0} pending`
  if (step.type === 'SUPERVISOR_REDIRECT') return `${step.data.targetNode || 'redirect'} · ${step.data.reason || 'sin razon'}`
  if (step.type === 'SUPERVISOR_OVERRIDE') return normalizeSupervisorCategory(step) || step.data.reason || 'Supervisor override'
  if (step.type === 'INTENT_TRAJECTORY_SIGNAL_DETECTED') {
    const patterns = Array.isArray(step.data.patterns)
      ? step.data.patterns.filter((entry: unknown): entry is string => typeof entry === 'string' && entry.trim().length > 0)
      : []
    return patterns.length > 0 ? `trajectory · ${patterns.join(', ')}` : 'Intent trajectory'
  }
  if (step.type === 'RUNTIME_SWITCH') return `${step.data.direction || 'switch'} · ${step.data.reason || 'sin razon'}`
  return cleanText(step.data.failReason) || step.type
}

export function getStepPresentation(step: TraceStep) {
  const config = getStepConfig(step.type)
  return {
    config,
    summary: summarizeStep(step),
    relatedFiles: getRelatedFiles(step),
    tools: getStepTools(step),
    llmAnalysis: getLlmAnalysis(step),
  }
}

function getStepHighlights(step: TraceStep): Record<string, unknown> {
  if (step.type === 'RUNTIME_ROUTING_DECISION') {
    return {
      intent: step.data.intent ?? null,
      runtime: step.data.runtime ?? null,
      reason: step.data.reason ?? null,
      tenantId: step.data.tenantId ?? null,
      conversationId: step.data.conversationId ?? null,
    }
  }

  if (step.type === 'INITIAL_GRAPH_ROUTE') {
    return {
      intent: step.data.intent ?? null,
      route: step.data.route ?? null,
      conversationId: step.data.conversationId ?? null,
    }
  }

  if (step.type === 'FINAL_EFFECTIVE_ROUTE') {
    return {
      initialRoute: step.data.initialRoute ?? null,
      finalRoute: step.data.finalRoute ?? null,
      escalated: Boolean(step.data.escalated),
      redirected: Boolean(step.data.redirected),
      escalationReason: step.data.escalationReason ?? null,
      supervisorOverride: step.data.supervisorOverride ?? null,
      conversationId: step.data.conversationId ?? null,
    }
  }

  if (
    step.type === 'GRAPH_CHECKPOINT_LOAD' ||
    step.type === 'GRAPH_CHECKPOINT_SAVE' ||
    step.type === 'GRAPH_CHECKPOINT_LOAD_FAILED' ||
    step.type === 'GRAPH_STATE_RESTORED'
  ) {
    return {
      threadId: step.data.threadId ?? null,
      checkpointId: step.data.checkpointId ?? null,
      restored: step.data.restored ?? null,
      saved: step.data.saved ?? null,
      reason: step.data.reason ?? step.data.error ?? null,
    }
  }

  if (step.type === 'LANGGRAPH_NODE_ENTER' || step.type === 'LANGGRAPH_NODE_EXIT') {
    return {
      nodeName: step.data.nodeName ?? null,
      turnId: step.data.turnId ?? null,
      stageBefore: step.data.stageBefore ?? null,
      stageAfter: step.data.stageAfter ?? null,
      durationMs: step.data.durationMs ?? null,
      failed: step.data.failed ?? false,
      error: step.data.error ?? null,
      supervisorOverridePresent: Boolean(step.data.supervisorOverridePresent),
    }
  }

  if (step.type === 'TOOL_EXECUTION' || step.type === 'TOOL_CALL' || step.type === 'CACHE') {
    return {
      tool: step.data.toolName ?? step.data.tool ?? null,
      cacheHit: Boolean(step.data.cacheHit),
      durationMs: step.data.durationMs ?? null,
      source: step.data.source ?? null,
      resultPreview: truncate(step.data.result, 120),
    }
  }

  if (
    step.type === 'ESCALATION_REQUESTED' ||
    step.type === 'ESCALATION_PROPAGATED' ||
    step.type === 'ESCALATION_HANDOFF_TRIGGERED'
  ) {
    return {
      source: step.data.source ?? null,
      reason: step.data.reason ?? null,
      conversationId: step.data.conversationId ?? null,
      tenantId: step.data.tenantId ?? null,
      messageId: step.data.messageId ?? null,
    }
  }

  if (step.type === 'REENGAGEMENT_SUGGESTED' || step.type === 'REENGAGEMENT_SCHEDULED' || step.type === 'REENGAGEMENT_CANCELLED') {
    return {
      stage: step.data.stage ?? null,
      stagnationTurns: step.data.stagnationTurns ?? null,
      timeoutMinutes: step.data.timeoutMinutes ?? null,
      hasProductContext: step.data.hasProductContext ?? null,
      onlyAfterPriceInquiry: step.data.onlyAfterPriceInquiry ?? null,
      reason: step.data.reason ?? null,
      cancelledCount: step.data.cancelledCount ?? null,
    }
  }

  if (step.type === 'SUPERVISOR_REDIRECT' || step.type === 'SUPERVISOR_OVERRIDE') {
    return {
      source: step.data.source ?? null,
      reason: step.data.reason ?? null,
      action: step.data.action ?? null,
      targetNode: step.data.targetNode ?? null,
      instruction: step.data.instruction ?? null,
      objectionSeverity: step.data.objectionSeverity ?? null,
      objectionLevel: step.data.objectionLevel ?? null,
      conversationId: step.data.conversationId ?? null,
    }
  }

  if (step.type === 'INTENT_TRAJECTORY_SIGNAL_DETECTED') {
    return {
      dominantIntent: step.data.dominantIntent ?? null,
      patterns: step.data.patterns ?? [],
      stage: step.data.stage ?? null,
      purchaseIntent: step.data.purchaseIntent ?? null,
      objectionLevel: step.data.objectionLevel ?? null,
    }
  }

  if (step.type === 'RUNTIME_SWITCH') {
    return {
      direction: step.data.direction ?? null,
      reason: step.data.reason ?? null,
      conversationId: step.data.conversationId ?? null,
    }
  }

  return {}
}

export function buildTraceDebugBundle(params: {
  tenant: Tenant | null
  conversation: Conversation | null
  trace: (TraceSummary & { steps: TraceStep[] }) | null
  history: TraceSummary[]
  liveMode: boolean
  activeTraceId: string | null
}) {
  const trace = params.trace
  const steps = trace?.steps || []
  const stepTypes = Array.from(new Set(steps.map((step) => step.type)))
  const toolNames = Array.from(new Set(steps.flatMap((step) => getStepTools(step))))
  const routeSteps = steps.filter((step) =>
    [
      'RUNTIME_ROUTING_DECISION',
      'INITIAL_GRAPH_ROUTE',
      'FINAL_EFFECTIVE_ROUTE',
      'GRAPH_CHECKPOINT_LOAD',
      'GRAPH_CHECKPOINT_SAVE',
      'GRAPH_CHECKPOINT_LOAD_FAILED',
      'GRAPH_STATE_RESTORED',
      'LANGGRAPH_NODE_ENTER',
      'LANGGRAPH_NODE_EXIT',
      'ESCALATION_REQUESTED',
      'ESCALATION_PROPAGATED',
      'ESCALATION_HANDOFF_TRIGGERED',
      'SUPERVISOR_REDIRECT',
      'SUPERVISOR_OVERRIDE',
      'RUNTIME_SWITCH',
    ].includes(step.type)
  )

  const escalationChain = steps.filter((step) =>
    ['ESCALATION_REQUESTED', 'ESCALATION_PROPAGATED', 'ESCALATION_HANDOFF_TRIGGERED', 'ESCALATE'].includes(step.type)
  )

  const stepEntries = steps.map((step, index) => {
    const presentation = getStepPresentation(step)

    return {
      index,
      type: step.type,
      label: presentation.config.label,
      color: presentation.config.color,
      icon: presentation.config.icon,
      ms: step.ms,
      summary: presentation.summary,
      tools: presentation.tools,
      relatedFiles: presentation.relatedFiles,
      highlights: getStepHighlights(step),
      rawData: step.data,
      llmAnalysis: presentation.llmAnalysis,
    }
  })

  const routeInfo = {
    initialRoute:
      steps.find((step) => step.type === 'INITIAL_GRAPH_ROUTE')?.data?.route ?? null,
    finalRoute:
      steps.find((step) => step.type === 'FINAL_EFFECTIVE_ROUTE')?.data?.finalRoute ?? null,
    runtimeDecision: steps.find((step) => step.type === 'RUNTIME_ROUTING_DECISION')?.data ?? null,
    checkpoint:
      steps.find((step) => step.type === 'GRAPH_CHECKPOINT_LOAD' || step.type === 'GRAPH_CHECKPOINT_LOAD_FAILED')?.data ?? null,
    graphStateRestored:
      steps.find((step) => step.type === 'GRAPH_STATE_RESTORED')?.data ?? null,
  }

  const metrics = {
    llmCalls: steps.filter((step) => step.type === 'LLM').length,
    toolCalls: steps.filter((step) => step.type === 'TOOL_CALL' || step.type === 'TOOL_EXECUTION').length,
    cacheHits: steps.filter((step) => step.type === 'CACHE' || Boolean(step.data?.cacheHit)).length,
    hasError: steps.some(
      (step) =>
        step.type === 'ERROR' ||
        step.type === 'GRAPH_CHECKPOINT_LOAD_FAILED' ||
        Boolean(step.data?.failed)
    ),
    isEscalated: steps.some(
      (step) =>
        step.type === 'ESCALATE' ||
        step.type === 'ESCALATION_HANDOFF_TRIGGERED' ||
        Boolean(step.data?.shouldEscalate)
    ),
  }

  const recommendedChecks = [
    steps.some((step) => step.type === 'GRAPH_CHECKPOINT_LOAD_FAILED')
      ? 'Checkpoint load failed. Verify DATABASE_URL and PostgresSaver availability.'
      : null,
    steps.some((step) => step.type === 'LANGGRAPH_NODE_EXIT' && Boolean(step.data?.failed))
      ? 'A LangGraph node failed. Inspect LANGGRAPH_NODE_EXIT and the related node file.'
      : null,
    steps.some((step) => step.type === 'ESCALATION_PROPAGATED') &&
    !steps.some((step) => step.type === 'ESCALATION_REQUESTED')
      ? 'Escalation propagated without an explicit request. Check orchestrator propagation ownership.'
      : null,
    steps.some((step) => step.type === 'ESCALATION_HANDOFF_TRIGGERED') &&
    !steps.some((step) => step.type === 'ESCALATION_PROPAGATED')
      ? 'Handoff triggered without propagation trace. Check trace emission ordering.'
      : null,
  ].filter((value): value is string => Boolean(value))

  return {
    exportKind: 'flow-debugger-trace-bundle',
    exportedAt: new Date().toISOString(),
    mode: params.liveMode ? 'live' : 'history',
    tenant: params.tenant,
    conversation: params.conversation,
    traceId: trace?.id ?? null,
    activeTraceId: params.activeTraceId,
    trace: trace
      ? {
          id: trace.id,
          messageId: trace.messageId ?? null,
          conversationId: trace.conversationId ?? params.conversation?.id ?? null,
          tenantId: trace.tenantId ?? params.tenant?.id ?? null,
          message: trace.message,
          strategy: trace.strategy ?? null,
          finalAction: trace.finalAction ?? null,
          shouldEscalate: trace.shouldEscalate ?? false,
          totalDurationMs: trace.totalDurationMs ?? null,
          totalTokensEst: trace.totalTokensEst ?? null,
          complexity: trace.complexity ?? null,
          complexityReason: (trace as any).complexityReason ?? null,
          failReason: trace.failReason ?? null,
          createdAt: trace.createdAt,
          traceJsonUrl: (trace as any).traceJsonUrl ?? null,
          liveTraceUrl: (trace as any).liveTraceUrl ?? null,
          thought: (trace as any).thought ?? null,
          plan: (trace as any).plan ?? null,
          reflection: (trace as any).reflection ?? null,
          buyerPsychology: (trace as any).buyerPsychology ?? null,
          conversionTrigger: (trace as any).conversionTrigger ?? null,
          negotiation: (trace as any).negotiation ?? null,
          closing: (trace as any).closing ?? null,
          postSale: (trace as any).postSale ?? null,
          intentRouter: (trace as any).intentRouter ?? null,
          intentTrajectory: (trace as any).intentTrajectory ?? null,
        }
      : null,
    history: params.history.map((item) => ({
      id: item.id,
      messageId: item.messageId ?? null,
      message: item.message,
      strategy: item.strategy ?? null,
      finalAction: item.finalAction ?? null,
      shouldEscalate: item.shouldEscalate ?? false,
      totalDurationMs: item.totalDurationMs ?? null,
      createdAt: item.createdAt,
      intentTrajectory: (item as any).intentTrajectory ?? null,
    })),
    summary: {
      stepCount: steps.length,
      stepTypes,
      toolNames,
      routeInfo,
      routeSteps: routeSteps.map((step) => ({
        type: step.type,
        ms: step.ms,
        data: getStepHighlights(step),
      })),
      escalationChain: escalationChain.map((step) => ({
        type: step.type,
        ms: step.ms,
        data: getStepHighlights(step),
      })),
      metrics,
      recommendedChecks,
    },
    steps: stepEntries,
    stepEntries,
  }
}



